import { Source } from '../../app/feathers/shared/Source';
import { Link } from '../../app/feathers/shared/Link';

/**
 * Creates a new Source from a previous Source when a Link has been updated.
 * Intelligently preserves paths, query parameters, and fragments while updating
 * hostname and protocol when appropriate.
 */
export const newSource = (previousSource: Source, newLink: Link, previousLink: Link): Source => {
  let newUrl: string = previousSource.url;
  let newTitle: string = previousSource.title || '';
  let newSourceType: 'website' | 'questionnaire' | 'other' = previousSource.sourceType;

  try {
    // Parse all URLs
    const sourceUrl = new URL(previousSource.url);
    const newLinkUrl = new URL(newLink.url);
    const previousLinkUrl = new URL(previousLink.url);

    // Check what changed between previousLink and newLink
    const hostnameChanged = previousLinkUrl.hostname !== newLinkUrl.hostname;
    const protocolChanged = previousLinkUrl.protocol !== newLinkUrl.protocol;
    const pathChanged = previousLinkUrl.pathname !== newLinkUrl.pathname;
    const searchChanged = previousLinkUrl.search !== newLinkUrl.search;

    // Build new URL for the source
    // Only update hostname/protocol if they changed; always preserve source's path/search/hash
    if (hostnameChanged) {
      // Update hostname (and protocol if it also changed), keep path, query, and fragment from source
      newUrl = `${newLinkUrl.protocol}//${newLinkUrl.hostname}${sourceUrl.pathname}${sourceUrl.search}${sourceUrl.hash}`;
    } else if (protocolChanged) {
      // Update protocol only, keep hostname, path, query, and fragment from source
      newUrl = `${newLinkUrl.protocol}//${sourceUrl.hostname}${sourceUrl.pathname}${sourceUrl.search}${sourceUrl.hash}`;
    }
    // If path or search changed in the link, don't change the source URL (keep original source URL)

    // Determine sourceType based on newLink's mediaType
    if (newLink.mediaType === 'website') {
      newSourceType = 'website';
    } else if (newLink.mediaType === 'questionnaire') {
      newSourceType = 'questionnaire';
    } else {
      newSourceType = 'other';
    }

    // Determine title based on newLink's mediaType
    if (newLink.mediaType === 'website') {
      newTitle = 'Campaign Website';
    } else if (
      newLink.mediaType === 'twitter' ||
      newLink.mediaType === 'facebook' ||
      newLink.mediaType === 'instagram' ||
      newLink.mediaType === 'youtube' ||
      newLink.mediaType === 'tiktok' ||
      newLink.mediaType === 'linkedin' ||
      newLink.mediaType === 'bluesky' ||
      newLink.mediaType === 'threads'
    ) {
      // Social media: capitalize first letter
      const mediaTypeStr = newLink.mediaType;
      const socialMediaTitles: Record<string, string> = {
        bluesky: 'Bluesky',
        linkedin: 'LinkedIn',
        threads: 'Threads',
        tiktok: 'TikTok',
        youtube: 'YouTube',
      };
      newTitle = socialMediaTitles[mediaTypeStr]
        || mediaTypeStr.charAt(0).toUpperCase() + mediaTypeStr.slice(1);
    } else {
      // Other: use newLink.title or default to "Article"
      newTitle = newLink.title || 'Article';
    }
  } catch (e) {
    // If URL parsing fails, fall back to simple replacement
    console.warn('Error parsing URLs in newSource:', e);
    newUrl = newLink.url;
  }

  return {
    ...previousSource,
    url: newUrl,
    title: newTitle,
    sourceType: newSourceType,
  };
};
